import React, { createContext, ReactNode, useContext, useMemo, useRef } from 'react';

type ScrollTarget = 'ocr-editor' | 'photo-guide' | 'select-document-type';

interface ScrollTargetContextType {
  scrollToTarget: (target: ScrollTarget) => void;
  registerRef: (target: ScrollTarget, ref: React.RefObject<HTMLDivElement>) => void;
}

const ScrollTargetContext = createContext<ScrollTargetContextType | undefined>(undefined);

interface ScrollTargetProviderProps {
  children: ReactNode;
}

export const ScrollTargetProvider: React.FC<ScrollTargetProviderProps> = ({ children }) => {
  const ocrEditorRef = useRef<HTMLDivElement>(null);
  const photoGuideRef = useRef<HTMLDivElement>(null);
  const selectDocumentTypeRef = useRef<HTMLDivElement>(null);

  const registerRef = (target: ScrollTarget, ref: React.RefObject<HTMLDivElement>) => {
    if (target === 'ocr-editor') {
      (ocrEditorRef as React.MutableRefObject<HTMLDivElement | null>).current = ref.current;
      return;
    }
    if (target === 'photo-guide') {
      (photoGuideRef as React.MutableRefObject<HTMLDivElement | null>).current = ref.current;
      return;
    }
    if (target === 'select-document-type') {
      (selectDocumentTypeRef as React.MutableRefObject<HTMLDivElement | null>).current =
        ref.current;
    }
  };

  const scrollToTarget = (target: ScrollTarget) => {
    let targetRef: React.RefObject<HTMLDivElement> | undefined;

    if (target === 'ocr-editor') {
      targetRef = ocrEditorRef;
    } else if (target === 'photo-guide') {
      targetRef = photoGuideRef;
    } else if (target === 'select-document-type') {
      targetRef = selectDocumentTypeRef;
    }

    setTimeout(() => {
      targetRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  };

  const value: ScrollTargetContextType = useMemo(
    () => ({
      scrollToTarget,
      registerRef,
    }),
    [],
  );

  return <ScrollTargetContext.Provider value={value}>{children}</ScrollTargetContext.Provider>;
};

export const useScrollTargetContext = (): ScrollTargetContextType => {
  const context = useContext(ScrollTargetContext);
  if (context === undefined) {
    throw new Error('useScrollTargetContext must be used within a ScrollTargetProvider');
  }
  return context;
};

export default ScrollTargetContext;
